import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Download, FileSpreadsheet, FileText } from 'lucide-react'
import type { ExportFormat } from '../api/admin.api'
import { badge, dropdown, item, root, trigger } from './ExportButton.css'

interface ExportButtonProps {
  onExport: (format: ExportFormat) => Promise<void>
  disabled?: boolean
}

export function ExportButton({ onExport, disabled = false }: ExportButtonProps) {
  const [open, setOpen] = useState(false)
  const [exporting, setExporting] = useState<ExportFormat | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [open])

  async function pickFormat(format: ExportFormat) {
    setOpen(false)
    setExporting(format)
    try {
      await onExport(format)
    } finally {
      setExporting(null)
    }
  }

  const busy = exporting !== null
  const isDisabled = disabled || busy

  return (
    <div className={root} ref={containerRef}>
      <button
        type="button"
        className={trigger}
        disabled={isDisabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Download size={13} strokeWidth={2.2} />
        {busy ? 'Exportando…' : 'Exportar'}
        <ChevronDown size={13} strokeWidth={2.2} />
      </button>

      {open && !isDisabled && (
        <div className={dropdown} role="menu">
          <button
            type="button"
            className={item}
            role="menuitem"
            onClick={() => {
              void pickFormat('xlsx')
            }}
          >
            <FileSpreadsheet size={14} />
            Planilha Excel
            <span className={badge}>XLSX</span>
          </button>
          <button
            type="button"
            className={item}
            role="menuitem"
            onClick={() => {
              void pickFormat('csv')
            }}
          >
            <FileText size={14} />
            Arquivo CSV
            <span className={badge}>CSV</span>
          </button>
        </div>
      )}
    </div>
  )
}
